import { useAdvisorSearch } from "@/components/shared/search/advisor-search";
import { useAffiliationSearch } from "@/components/shared/search/affiliation-search";
import { useAgentSearch } from "@/components/shared/search/agent-search";
import { useCarrierSearch } from "@/components/shared/search/carrier-search";
import { useCaseManagerSearch } from "@/components/shared/search/case-manager-search";
import { useContractPartySearch } from "@/components/shared/search/contract-party-search";
import { useMarketingManagerSearch } from "@/components/shared/search/marketing-manager-search";
import { usePayorSearch } from "@/components/shared/search/payor-search";
import { usePolicySearch } from "@/components/shared/search/policy-search";
import { useProductSearch } from "@/components/shared/search/product-search";
import {
  mdiAccount,
  mdiAccountCheck,
  mdiAccountTie,
  mdiBookOpenPageVariant,
  mdiCube,
  mdiDomain,
  mdiPound
} from "@mdi/js";

export default class TableHeader {
  align = null;
  displayMap = "";
  filter = undefined;
  filterable = undefined;
  filterOrder = 99;
  filterType = null;
  hideDisplayValue = false;
  icon = "";
  inputProps = null;
  isAdditional = false;
  isMandatory = false;
  isQueryFilter = false;
  map = "";
  order = 99;
  sortKey = "";
  allowedFalseyValues = [];
  width = null;
  sortRaw = undefined;
  isCurrency = false;
  futureDateValues = false;
  selectableOptions = [];
  selectableOptionsFn = [];
  sendFalse = false;
  showIf = null;
  showInTableIf = null;
  sortable = false;
  sortFilterMap = "";
  text = "";
  value = "";

  static NUMBER_TYPE = "number";
  static STRING_TYPE = "string";
  static DATE_TYPE = "date";
  static ACTION_TYPE = "action";
  static HIDDEN_TYPE = "hidden";
  static AUTOCOMPLETE_TYPE = "autocomplete";
  static SELECT_TYPE = "select";
  static MULTI_SELECT_TYPE = "MULTI_SELECT";
  static CHECKBOX_TYPE = "checkbox";

  static NUMBER_RANGE = "NUMBER_RANGE";
  static GLOBAL_SEARCH_FILTER_TYPE = "GLOBAL_SEARCH_FILTER_TYPE";
  static PHONE_TYPE = "PHONE_TYPE";

  static GENERIC_SEARCH = "GENERIC_SEARCH";
  static CACHEABLE_SEARCH = "CACHEABLE_SEARCH";

  // Helpers
  static IS_SORTABLE = { sortable: true };
  static IS_FILTERABLE = { filterable: true };
  static IS_QUERY_FILTER = { isQueryFilter: true };
  static IS_MANDATORY = { isMandatory: true };
  static IS_ADDITIONAL = { isAdditional: true };

  static IS_DATE_FILTER_TYPE = {
    filterType: this.DATE_TYPE,
    inputProps: { debounced: true }
  };
  static IS_ACTION_FILTER_TYPE = { filterType: this.ACTION_TYPE };
  static IS_HIDDEN_FILTER_TYPE = { filterType: this.HIDDEN_TYPE };
  static IS_AUTOCOMPLETE_FILTER_TYPE = { filterType: this.AUTOCOMPLETE_TYPE };

  static IS_NUMBER_RANGE_TYPE = {
    filterType: this.NUMBER_RANGE,
    inputProps: {
      debounced: true
    }
  };

  static IS_CURRENCY_RANGE_TYPE = {
    filterType: this.NUMBER_RANGE,
    inputProps: {
      isCurrency: true,
      debounced: true
    }
  };

  static IS_CHECKBOX_TYPE = {
    filterType: this.CHECKBOX_TYPE,
    inputProps: {
      trueValue: true,
      falseValue: false
    }
  };

  static IS_NUMBER_FILTER_TYPE = {
    filterType: this.STRING_TYPE,
    inputProps: {
      inputmode: "numeric",
      debounced: true
    }
  };

  static IS_STRING_FILTER_TYPE = {
    filterType: this.STRING_TYPE,
    inputProps: {
      inputmode: "text",
      debounced: true
    }
  };

  static SEND_FALSE = { sendFalse: true };
  static SEND_EMPTY_STRING = { sendEmptyString: true };

  static IS_GLOBAL_SEARCH_FILTER_TYPE = {
    filterType: this.GLOBAL_SEARCH_FILTER_TYPE
  };

  static IS_PHONE_FILTER_TYPE = {
    filterType: this.PHONE_TYPE
  };

  static IS_SELECT_FILTER_TYPE = {
    filterType: this.SELECT_TYPE
  };

  static IS_MULTI_SELECT_TYPE = {
    filterType: this.SELECT_TYPE,
    inputProps: { multiple: true, debounced: true }
  };

  // GENERIC SEARCHES

  static IS_AFFILIATION_SEARCH_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiBookOpenPageVariant,
    inputProps: {
      composable: useAffiliationSearch
    }
  };

  static IS_ADVISOR_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccount,
    inputProps: {
      searchArgs: { simple: true },
      composable: useAdvisorSearch
    }
  };

  static IS_ADVISOR_PAYMENT_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccount,
    inputProps: {
      searchArgs: { payment: true },
      composable: useAdvisorSearch
    }
  };

  static IS_PAYOR_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccountCheck,
    inputProps: {
      composable: usePayorSearch
    }
  };

  static IS_POLICY_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiPound,
    inputProps: {
      searchArgs: { simple: true },
      composable: usePolicySearch
    }
  };

  static IS_CONTRACT_PARTY_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccount,
    inputProps: {
      searchArgs: { simple: true },
      composable: useContractPartySearch
    }
  };

  static IS_CARRIER_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiDomain,
    inputProps: {
      composable: useCarrierSearch
    }
  };

  static IS_AGENT_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccount,
    inputProps: {
      composable: useAgentSearch
    }
  };

  static IS_PRODUCT_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiCube,
    inputProps: {
      composable: useProductSearch
    }
  };

  static IS_CASE_MANAGER_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccountTie,
    inputProps: {
      searchArgs: { simple: true },
      composable: useCaseManagerSearch
    }
  };

  static IS_MARKETING_MANAGER_SEARCH_FILTER_TYPE = {
    filterType: this.GENERIC_SEARCH,
    icon: mdiAccountTie,
    inputProps: {
      composable: useMarketingManagerSearch
    }
  };

  // CACHEABLE SEARCHES

  static IS_FORM_SET_SEARCH = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getFormSets",
      cacheItemsKey: "formSets"
    }
  };

  static IS_VENDOR_SEARCH_TYPE = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getVendors",
      cacheItemsKey: "vendors"
    }
  };

  static IS_PRICING_PLAN_SEARCH_TYPE = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getPricingPlans",
      cacheItemsKey: "pricingPlans"
    }
  };

  static IS_FORM_CATEGORY_SEARCH = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getFormCategories",
      cacheItemsKey: "formCategories"
    }
  };

  static IS_PRODUCT_TYPE_SEARCH = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getProductMeta",
      cacheItemsKey: "productTypes",
      debounced: true
    }
  };

  static IS_PRODUCT_TRAIT_SEARCH = {
    filterType: this.CACHEABLE_SEARCH,
    inputProps: {
      cacheFetchKey: "getProductMeta",
      cacheItemsKey: "productTraits",
      multiple: true,
      debounced: true
    }
  };

  static TOTAL_ORDER = order => ({ order, filterOrder: order });

  constructor({
    text = "",
    value = "",
    map = "",
    filterType = TableHeader.STRING_TYPE,
    selectableOptions = [],
    selectableOptionsFn = null,
    sortable = false,
    filterable = false,
    isMandatory = false,
    isQueryFilter = false,
    sortFilterMap = "",
    isAdditional = false,
    displayMap = "",
    icon = "",
    showIf = null,
    order = 99,
    filterOrder = 99,
    sortKey = "",
    sendFalse = false,
    sendEmptyString = false,
    align = null,
    width = null,
    filter = null,
    sortRaw = null,
    showInTableIf = null,
    hideDisplayValue = false,
    inputProps = null
  }) {
    this.text = text;
    this.value = value;
    this.map = map;
    this.filterType = filterType;
    this.selectableOptions = selectableOptions;
    this.selectableOptionsFn = selectableOptionsFn;
    this.sortable = sortable;
    this.filterable = filterable;
    this.isMandatory = isMandatory;
    this.isQueryFilter = isQueryFilter;
    this.sortFilterMap = sortFilterMap;
    this.isAdditional = isAdditional;
    this.displayMap = displayMap;
    this.icon = icon;
    this.showIf = showIf;
    this.order = order;
    this.filterOrder = filterOrder;
    this.sortKey = sortKey;
    this.align = align;
    this.width = width;
    this.filter = filter;
    this.sortRaw = sortRaw;
    this.showInTableIf = showInTableIf;
    this.hideDisplayValue = hideDisplayValue;
    if (sendFalse) {
      this.allowedFalseyValues.push(false);
    }
    if (sendEmptyString) {
      this.allowedFalseyValues.push("");
    }
    this.inputProps = inputProps;
  }
}
